class TestimonialsCarousel {
  constructor(element) {
    this.carousel = element;
    this.track = this.carousel.querySelector('.testimonials-track');
    this.cards = Array.from(this.track.querySelectorAll('.testimonial-card'));
    this.prevBtn = this.carousel.parentElement.querySelector('.carousel-nav-prev');
    this.nextBtn = this.carousel.parentElement.querySelector('.carousel-nav-next');
    this.dotsContainer = this.carousel.parentElement.parentElement.querySelector('.carousel-dots');
    
    this.currentIndex = 0;
    this.cardsPerView = this.getCardsPerView();
    this.totalPages = Math.max(1, Math.ceil(this.cards.length / this.cardsPerView));
    
    this.init();
  }

  init() {
    if (this.cards.length === 0) return;
    
    this.createDots();
    this.updateCarousel();
    this.setupEventListeners();
    this.setupReadMore();
    this.handleResize();
  }

  getCardsPerView() {
    const width = window.innerWidth;
    if (width > 989) return 3;
    if (width > 749) return 2;
    return 1;
  }

  createDots() {
    if (!this.dotsContainer) return;
    
    this.dotsContainer.innerHTML = '';
    
    // No crear dots si solo hay una página
    if (this.totalPages <= 1) return;
    
    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.classList.add('carousel-dot');
      dot.setAttribute('aria-label', `Ir a página ${i + 1}`);
      if (i === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  updateCarousel() {
    if (!this.track || this.cards.length === 0) return;
    
    // Asegurar que el índice esté dentro de los límites
    const maxIndex = Math.max(0, this.totalPages - 1);
    this.currentIndex = Math.min(Math.max(0, this.currentIndex), maxIndex);
    
    const firstCard = this.cards[0];
    if (!firstCard) return;
    
    const cardWidth = firstCard.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    const offset = -(this.currentIndex * this.cardsPerView * (cardWidth + gap));
    
    this.track.style.transform = `translateX(${offset}px)`;
    
    // Update navigation buttons
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }
    
    // Update dots
    const dots = this.dotsContainer?.querySelectorAll('.carousel-dot');
    if (dots) {
      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentIndex);
      });
    }
  }

  goToPage(pageIndex) {
    const maxIndex = Math.max(0, this.totalPages - 1);
    this.currentIndex = Math.max(0, Math.min(pageIndex, maxIndex));
    this.updateCarousel();
  }

  next() {
    const maxIndex = Math.max(0, this.totalPages - 1);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
      this.updateCarousel();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
    }
  }

  setupEventListeners() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.next();
      });
    }

    // Keyboard navigation
    this.carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });

    // Touch support
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.carousel.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.carousel.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  setupReadMore() {
    const readMoreBtns = this.carousel.querySelectorAll('.read-more-btn');
    
    readMoreBtns.forEach(btn => {
      const textElement = btn.previousElementSibling;
      if (!textElement) return;
      
      const fullText = textElement.getAttribute('data-full-text');
      if (!fullText) return;
      
      // Limpiar el texto de spans existentes primero
      const cleanFullText = fullText.replace(/<span class="first-letter">(.*?)<\/span>/, '$1');
      
      const shortText = this.truncateText(cleanFullText, 200);
      
      // Aplicar wrapFirstLetter SOLO UNA VEZ
      const wrappedShortText = this.wrapFirstLetter(shortText);
      const wrappedFullText = this.wrapFirstLetter(cleanFullText);
      
      // Set initial state
      textElement.innerHTML = wrappedShortText;
      textElement.classList.add('collapsed');
      textElement.classList.remove('expanded');
      
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isCollapsed = textElement.classList.contains('collapsed');
        
        if (isCollapsed) {
          // Expandir - usar texto completo ya formateado
          textElement.innerHTML = wrappedFullText;
          textElement.classList.remove('collapsed');
          textElement.classList.add('expanded');
          btn.textContent = 'Ver menos';
        } else {
          // Colapsar - usar texto corto ya formateado
          textElement.innerHTML = wrappedShortText;
          textElement.classList.remove('expanded');
          textElement.classList.add('collapsed');
          btn.textContent = 'Ver más';
        }
      });
    });
  }

  wrapFirstLetter(text) {
    if (!text || text.length === 0) return text;
    
    // Si ya tiene un span first-letter, no hacer nada
    if (text.includes('first-letter')) return text;
    
    const firstLetter = text.charAt(0);
    const restOfText = text.slice(1);
    return `<span class="first-letter">${firstLetter}</span>${restOfText}`;
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    
    // Truncar en el último espacio antes del límite para no cortar palabras
    let truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > 0) {
      truncated = truncated.substring(0, lastSpace);
    }
    
    return truncated.trim() + '...';
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newCardsPerView = this.getCardsPerView();
        if (newCardsPerView !== this.cardsPerView) {
          this.cardsPerView = newCardsPerView;
          this.totalPages = Math.max(1, Math.ceil(this.cards.length / this.cardsPerView));
          
          // Ajustar el índice actual si es necesario
          const maxIndex = Math.max(0, this.totalPages - 1);
          this.currentIndex = Math.min(this.currentIndex, maxIndex);
          
          this.createDots();
        }
        this.updateCarousel();
      }, 250);
    });
  }
}

// Initialize all carousels on the page
function initTestimonialsCarousels() {
  const carousels = document.querySelectorAll('.testimonials-carousel');
  carousels.forEach(carousel => {
    // Evitar inicializar dos veces
    if (!carousel.hasAttribute('data-carousel-initialized')) {
      carousel.setAttribute('data-carousel-initialized', 'true');
      new TestimonialsCarousel(carousel);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTestimonialsCarousels);
} else {
  initTestimonialsCarousels();
}

// Reinitialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    setTimeout(() => {
      const carousel = event.target.querySelector('.testimonials-carousel');
      if (carousel) {
        carousel.removeAttribute('data-carousel-initialized');
        new TestimonialsCarousel(carousel);
      }
    }, 100);
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => {
      document.querySelectorAll('.testimonials-carousel').forEach(c => {
        c.removeAttribute('data-carousel-initialized');
      });
      initTestimonialsCarousels();
    }, 100);
  });
}